@use "../../../scss/settings/colors";
@use "../../../scss/settings/breakpoints";

// Category accents: icon colour + soft tile background
$accent-purple: colors.$brand-purple;
$accent-purple-bg: #f3ebff;
$accent-blue: colors.$brand-blue;
$accent-blue-bg: #e8f2fd;
$accent-green: #10a36d;
$accent-green-bg: #e7f8f0;
$accent-amber: #a8792c;
$accent-amber-bg: #fdf3e2;

$count-color: colors.$gray-variant-16;
$description-color: #4f5b76;

// The grid sizes its columns from the space it actually has (not the viewport),
// so it works in full-width pages and in narrower profile layouts alike.
.grid_container {
  container-type: inline-size;
  width: 100%;
}

.grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  background-color: transparent;
}

@container (min-width: 600px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }
}

@container (min-width: 1240px) {
  .grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.card {
  container-type: inline-size;
  position: relative;
  box-sizing: border-box;
  padding: 20px 28px 20px 20px;
  background-color: colors.$base-white;
  border: 1px solid transparent;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(21, 42, 88, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

}

// Category cards (with a description) keep the taller proportions from the design;
// workflow cards (title + count only) stay compact.
.with_description {
  min-height: 210px;

  @media #{breakpoints.$mobile} {
    min-height: 0;
  }
}

// Reserve room for the info icon; it is hidden below desktop, so drop the space there.
.with_preview {
  padding-bottom: 36px;

  @media #{breakpoints.$tablet-mobile} {
    padding-bottom: 20px;
  }
}

.clickable {
  cursor: pointer;

  &:hover {
    border-color: colors.$purple-variant;
    box-shadow: 0 6px 18px rgba(100, 20, 219, 0.1);
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 2px;
  }
}

.layout {
  display: flex;
  align-items: flex-start;
  gap: 22px;
}

.icon_container {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 90px;
  height: 86px;
  border-radius: 12px;

  svg {
    width: 46px;
    height: 46px;
  }

  // Source SVGs ship with hard-coded colours and mix filled and stroked artwork.
  // Recolour every painted fill/stroke with the category colour while leaving
  // "none" and white (clip-path masks) untouched.
  svg:not([fill]),
  svg[fill]:not([fill='none']):not([fill='white']):not([fill='#fff']):not([fill='#ffffff']),
  svg [fill]:not([fill='none']):not([fill='white']):not([fill='#fff']):not([fill='#ffffff']) {
    fill: currentColor;
  }

  svg [stroke]:not([stroke='none']) {
    stroke: currentColor;
  }

  // Filled outline icons are drawn quite thin; a hairline stroke gives them the
  // weight shown in the design. Non-scaling keeps it even across icon viewBoxes.
  svg path[fill]:not([fill='none']):not([fill='white']):not([stroke]) {
    stroke: currentColor;
    stroke-width: 1px;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;
  }
}

.accent_purple .icon_container {
  color: $accent-purple;
  background-color: $accent-purple-bg;
}

.accent_blue .icon_container {
  color: $accent-blue;
  background-color: $accent-blue-bg;
}

.accent_green .icon_container {
  color: $accent-green;
  background-color: $accent-green-bg;
}

.accent_amber .icon_container {
  color: $accent-amber;
  background-color: $accent-amber-bg;
}

.content {
  min-width: 0;
  padding-top: 10px;
}

.title_row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.title {
  margin: 0;
  font-family: 'SemiBold Inter', sans-serif;
  font-size: 16px;
  font-weight: normal;
  line-height: 1.3;
  color: colors.$dark-blue;
}

.subtitle {
  margin: 4px 0 0;
  font-family: 'Regular Inter', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: $description-color;
  overflow-wrap: anywhere;
}

.count {
  margin: 4px 0 0;
  font-family: 'Medium Inter', sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: $count-color;
}

.description {
  margin: 18px 0 0;
  font-family: 'Regular Inter', sans-serif;
  font-size: 13.5px;
  line-height: 1.7;
  color: $description-color;
}

.new_label,
.free_label {
  display: inline-flex;
  padding: 2px 8px;
  font-family: 'Medium Inter', sans-serif;
  font-size: 11px;
  line-height: 1.5;
  color: colors.$dark-blue;
  background-color: colors.$blue-variant-2;
  border-radius: 999px;
}

.free_label {
  background-color: colors.$green-variant-light;
}

.preview_icon {
  position: absolute;
  right: 16px;
  bottom: 14px;
  cursor: help;

  @media #{breakpoints.$tablet-mobile} {
    display: none !important;
  }
}

.actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display:flex;
  align-items: center;
  gap: 2px;
}

.with_actions .content{
  padding-top: 30px;
}

.with_actions .title{
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-clamp: 2;
  min-width: 0;
}
// When a card is too narrow for the side-by-side layout, stack the icon above the text.
@container (max-width: 250px) {
  .layout {
    flex-direction: column;
    gap: 14px;
  }

  .icon_container {
    width: 64px;
    height: 62px;

    svg {
      width: 34px;
      height: 34px;
    }
  }

  .content {
    padding-top: 0;
  }
}
